/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { Input } from "@coreai/component-library";
import { useLanguage } from "LanguageContext";

interface NotesFieldProps {
  value: string;
  onChange: (value: string) => void;
  name?: string;
}

export default function NotesField({
  value,
  onChange,
  name = "note",
}: Readonly<NotesFieldProps>) {
  const { t } = useLanguage();
  const theme = useTheme();
  const fieldRef = useRef<HTMLDivElement>(null);
  const openedByClick = useRef(false);
  const [visible, setVisible] = useState(Boolean(value.trim()));

  useEffect(() => {
    if (value.trim()) {
      setVisible(true);
    }
  }, [value]);

  useEffect(() => {
    if (visible && openedByClick.current) {
      fieldRef.current?.querySelector("input")?.focus();
      openedByClick.current = false;
    }
  }, [visible]);

  const revealNotes = () => {
    openedByClick.current = true;
    setVisible(true);
  };

  const fieldStyles = css`
    width: 100%;
  `;

  const buttonSlotStyles = css`
    display: flex;
    align-items: flex-end;
    width: 100%;
    min-height: 100%;
  `;

  const addNotesButtonStyles = css`
    box-sizing: border-box;
    height: 42px;
    margin: 0;
    padding: 0 24px;
    border: 1.5px solid #1a1a1a;
    border-radius: 999px;
    background: ${theme.colors.white};
    color: #1a1a1a;
    font-family: ${theme.typography.body.fontFamily};
    font-size: ${theme.typography.body.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    line-height: ${theme.typography.body.lineHeight};
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      background: ${theme.colors.grey};
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary};
      outline-offset: 2px;
    }
  `;

  return (
    <div ref={fieldRef} css={visible ? fieldStyles : buttonSlotStyles}>
      {visible ? (
        <Input
          name={name}
          label={t("analyse_form_label_note")}
          value={value}
          onChange={(ev) => onChange(ev.target.value)}
          autoComplete="off"
          style={{ width: "100%" }}
        />
      ) : (
        <button type="button" css={addNotesButtonStyles} onClick={revealNotes}>
          {t("analyse_form_add_notes")}
        </button>
      )}
    </div>
  );
}
